import axios from "axios";
import type { QuestionContentData } from "@/lib/types/question-content";

const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '';

/**
 * Admit API origin for passport photo fetch. Prefer `NEXT_PUBLIC_ADMIT_API_URL`.
 * - Staging/prod: if unset, derive from `NEXT_PUBLIC_API_URL` by replacing `suexam` → `suadm` in the host.
 * - Local: if host is localhost/127.0.0.1 and Admit URL is unset, use port `NEXT_PUBLIC_ADMIT_API_PORT` (default 3000)
 *   so Exam API on :3500 and Admit on :3000 works without a second full URL.
 */
function getAdmitApiBaseUrl(): string {
  const explicit = (process.env.NEXT_PUBLIC_ADMIT_API_URL || "").trim().replace(/\/$/, "");
  if (explicit) return explicit;
  const examBase = (process.env.NEXT_PUBLIC_API_URL || "").trim().replace(/\/$/, "");
  if (!examBase) return "";
  try {
    const u = new URL(examBase);
    // suexam-stg.suh.edu.in → suadm-stg.suh.edu.in (and similar)
    const host = u.hostname.replace(/suexam/gi, "suadm");
    if (host !== u.hostname) {
      return `${u.protocol}//${host}${u.port ? `:${u.port}` : ""}`;
    }
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") {
      const port = (process.env.NEXT_PUBLIC_ADMIT_API_PORT || "3000").replace(/^:/, "");
      return `${u.protocol}//${u.hostname}:${port}`;
    }
    // Already on Admit host (suadm / admit360)
    if (/suadm|admit/i.test(u.hostname)) {
      return `${u.protocol}//${u.hostname}${u.port ? `:${u.port}` : ""}`;
    }
  } catch {
    return "";
  }
  return "";
}

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface Question {
  questionId: string;
  type: "multiple-choice" | "true-false" | "short-answer" | "essay";
  question: QuestionContentData;
  options?: QuestionContentData[];
  correctAnswer?: string | string[];
  points: number;
  order: number;
  isAnswerEditor?: boolean;
  answerMode?: "typed" | "handwritten";
  showWordCount?: boolean;
  showCharacterCount?: boolean;
  maxWords?: number;
  maxCharacters?: number;
}

export interface ExamSetInfo {
  _id: string;
  setCode: string;
  questionCount: number;
}

export interface ExamRegistrationStudent {
  applicantId: string;
  applicationNo: string;
  name: string;
  email?: string;
  attemptStatus?: string;
  assignedAt?: string | null;
}

export interface Exam {
  _id: string;
  id?: string; // Some responses use 'id' instead of '_id'
  code: string;
  name: string;
  title?: string; // For backward compatibility, map name to title
  description: string;
  durationMinutes: string | number; // Can be "180" or "01:30" format
  duration?: number; // Parsed duration in minutes (writing time)
  /** Extra minutes after writing for handwritten uploads (PhD). 0 = none. */
  uploadWindowMinutes?: number;
  subjectsConfig?: Array<{
    subject: string;
    distribution: {
      EASY: number;
      MEDIUM: number;
      HARD: number;
    };
  }>;
  totalQuestions?: number;
  marksPerQuestion?: number;
  negativeMarking?: number;
  numberOfSets?: number;
  uniqueQuestionsAcrossSets?: boolean;
  startTime?: string;
  endTime?: string;
  resultVisibility?: string;
  status?: string;
  isActive: boolean;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  /** Practice/mock exams skip ID photo flow and go straight to the attempt UI */
  isMock?: boolean;
  enrolledCount?: number;
  // One set per exam (for student dashboard; no ExamSession)
  set?: ExamSetInfo | null;
  // Optional fields for backward compatibility
  questions?: Question[];
  totalPoints?: number;
  settings?: {
    randomizeQuestions?: boolean;
    randomizeOptions?: boolean;
    allowBackNavigation?: boolean;
    fullscreenRequired?: boolean;
    cameraRequired?: boolean;
    microphoneRequired?: boolean;
    disableHeadMovementWarningForWritten?: boolean;
  };
}

export interface RegistrationPhotoResponse {
  passportPhotoUrl: string | null;
}

export interface SessionRegisteredPhotoResponse {
  registeredPhoto: string | null;
}

export interface ExamSession {
  sessionId: string;
  examId: string;
  startTime: string;
  /** When writing duration started (first question). May equal startTime after begin-writing-clock. */
  writingStartTime?: string | null;
  endTime?: string | null;
  submittedAt?: string | null;
  status: string;
  violationScore: number;
  riskLevel: string;
  applicationNo?: string;
  programLevel?: string;
  studentId?:
    | {
        name?: string;
        email?: string;
        studentId?: string;
      }
    | string;
  answers?: Record<string, { answer?: string | string[] } | string | string[]>;
  registeredPhoto?: string | null;
}

export interface ExamQuestionResult {
  questionId: string;
  subject?: string | null;
  isCorrect: boolean;
  isAnswered: boolean;
  marksObtained: number;
  studentAnswer?: unknown;
  correctAnswer?: unknown;
}

export interface ExamResultsPayload {
  examId: string;
  studentId: string;
  userId?: string;
  sessionId?: string;
  examName?: string | null;
  isMock?: boolean;
  totalQuestions: number;
  attempted: number;
  correctCount: number;
  wrongCount: number;
  unansweredCount: number;
  marksPerQuestion: number;
  negativeMarking: number;
  maxScore: number;
  totalScore: number;
  percentage: number;
  submittedAt?: string | null;
  detailedResults?: ExamQuestionResult[];
}

export interface ExamSubmitResponse {
  message?: string;
  isMock?: boolean;
  immediateResults?: boolean;
  data?: {
    sessionId?: string;
    examId?: string;
    submittedAt?: string;
    results?: ExamResultsPayload | null;
  };
  results?: ExamResultsPayload | null;
}

export interface LivekitTokenResponse {
  url: string;
  token: string;
  roomName: string;
  participantIdentity: string;
  mode: "publisher" | "subscriber";
}

type BackendExamPayload = Partial<Exam> & Record<string, unknown>;
type MonitorSessionPayload = Record<string, unknown>;

// Helper function to parse duration from string format
const parseDuration = (durationMinutes: string | number): number => {
  if (typeof durationMinutes === 'number') {
    return durationMinutes;
  }
  // Handle "01:30" format (HH:MM)
  if (durationMinutes.includes(':')) {
    const [hours, minutes] = durationMinutes.split(':').map(Number);
    return hours * 60 + minutes;
  }
  // Handle plain number string like "180"
  return parseInt(durationMinutes, 10) || 0;
};

// Helper function to map backend exam to frontend format
const mapExamFromBackend = (exam: BackendExamPayload): Exam => {
  const examId = String(exam._id ?? exam.id ?? "");
  const examName = typeof exam.name === "string" ? exam.name : "";
  const examTitle = typeof exam.title === "string" ? exam.title : undefined;
  const examDescription = typeof exam.description === "string" ? exam.description : "";
  const isActive = typeof exam.isActive === "boolean" ? exam.isActive : true;
  const rawDuration =
    typeof exam.durationMinutes === "string" || typeof exam.durationMinutes === "number"
      ? exam.durationMinutes
      : typeof exam.duration === "number"
        ? exam.duration
        : 0;

  return {
    ...exam,
    _id: examId,
    code: typeof exam.code === "string" ? exam.code : "",
    name: examName,
    description: examDescription,
    durationMinutes: rawDuration,
    uploadWindowMinutes: Math.max(0, Number(exam.uploadWindowMinutes) || 0),
    isActive,
    title: examName || examTitle, // Map name to title for compatibility
    duration: parseDuration(rawDuration),
    id: typeof exam.id === "string" ? exam.id : examId,
  };
};

export const examService = {
  // Get all exams (proctors receive only their assigned exams — all of them)
  getExams: async (): Promise<Exam[]> => {
    const response = await api.get("/api/exams");
    const exams = (response.data.exams || []).map(mapExamFromBackend);
    // Dedupe by id in case assignment rows map to the same exam more than once
    const seen = new Set<string>();
    return exams.filter((exam: Exam) => {
      const key = String(exam._id || exam.id || "");
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  },

  /** Check whether the current user may open the exam monitor. */
  checkMonitorAccess: async (
    examId: string,
  ): Promise<{
    allowed: boolean;
    mode?: string;
    applicantIds?: string[] | null;
    applicationNos?: string[] | null;
    error?: string;
  }> => {
    try {
      const response = await api.get(`/api/exams/${encodeURIComponent(examId)}/monitor-access`);
      const allowed = Boolean(response.data?.allowed);
      if (!allowed) {
        return {
          allowed: false,
          error: response.data?.error || "Access is denied",
        };
      }
      return {
        allowed: true,
        mode: response.data?.mode,
        applicantIds: response.data?.applicantIds ?? null,
        applicationNos: response.data?.applicationNos ?? null,
      };
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: { error?: string } } };
      if (err.response?.status === 403 || err.response?.status === 401) {
        return {
          allowed: false,
          error: err.response.data?.error || "Access is denied",
        };
      }
      throw error;
    }
  },

  // Get exams with one set per exam (for student dashboard; no ExamSession)
  getExamsForDashboard: async (): Promise<Exam[]> => {
    const response = await api.get("/api/exams/dashboard");
    const exams = ((response.data.exams || []) as BackendExamPayload[]);
    return exams.map((exam) => {
      const setInfo =
        typeof exam === "object" && exam !== null && "set" in exam
          ? (exam as { set?: ExamSetInfo | null }).set ?? null
          : null;
      return {
        ...mapExamFromBackend(exam),
        set: setInfo,
      };
    });
  },

  // Create exam
  createExam: async (examData: {
    title: string;
    description?: string;
    duration: number;
    questions: Question[];
    passingScore?: number;
    settings?: {
      randomizeQuestions?: boolean;
      randomizeOptions?: boolean;
      allowBackNavigation?: boolean;
      fullscreenRequired?: boolean;
      cameraRequired?: boolean;
      microphoneRequired?: boolean;
      disableHeadMovementWarningForWritten?: boolean;
    };
  }): Promise<Exam> => {
    const response = await api.post("/api/exams", examData);
    return response.data.exam;
  },

  // Get exam by ID
  getExamById: async (examId: string): Promise<Exam> => {
    const response = await api.get(`/api/exams/${examId}`, { timeout: 10000 });
    return mapExamFromBackend(response.data.exam);
  },

  /** Registered students for admin Students Enrolled modal */
  getExamRegistrations: async (examId: string): Promise<ExamRegistrationStudent[]> => {
    const response = await api.get(`/api/exams/${examId}/registrations`);
    return (response.data.registrations || []) as ExamRegistrationStudent[];
  },

  /**
   * JWT-protected image bytes from SU-Admit360 (reads `uploads/` + exam registration path).
   * Set `NEXT_PUBLIC_ADMIT_API_URL` to the Admit API origin (not the Exam360 URL).
   */
  fetchMyRegistrationPassportPhotoBlobUrl: async (
    examId: string,
    signal?: AbortSignal
  ): Promise<string | null> => {
    const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
    if (!token) return null;
    const admitBase = getAdmitApiBaseUrl();
    if (!admitBase) {
      console.warn(
        "[registration photo] Set NEXT_PUBLIC_ADMIT_API_URL, or use localhost with NEXT_PUBLIC_ADMIT_API_PORT (default 3000), or a suexam host so Admit URL can be derived (suexam → suadm)."
      );
      return null;
    }
    const url = `${admitBase}/api/exams/${examId}/my-registration-passport-photo`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        console.warn(
          "[registration photo] Unauthorized — Exam360 and SU-Admit360 must use the same JWT secret (SECRET_KEY / JWT_SECRET) for student tokens."
        );
      } else if (res.status === 404) {
        try {
          const j = (await res.json()) as { error?: string };
          console.warn("[registration photo]", j.error || res.statusText);
        } catch {
          /* ignore */
        }
      } else {
        console.warn("[registration photo] HTTP", res.status, url);
      }
      return null;
    }
    const blob = await res.blob();
    const ct = blob.type || "";
    if (ct && !ct.startsWith("image/") && ct !== "application/octet-stream") {
      return null;
    }
    return URL.createObjectURL(blob);
  },

  getMyRegistrationPhotoUrl: async (
    examId: string,
  ): Promise<RegistrationPhotoResponse> => {
    const response = await api.get(`/api/exams/${examId}/my-registration-photo`);
    return response.data as RegistrationPhotoResponse;
  },

  // Get exam with questions for a set (student attempt). Pass setCode when from dashboard; omit to use first set.
  getExamWithSetForAttempt: async (examId: string, setCode?: string): Promise<Exam> => {
    const response = setCode
      ? await api.get(`/api/exams/${examId}/sets/${encodeURIComponent(setCode)}/attempt`)
      : await api.get(`/api/exams/${examId}/attempt`);
    return mapExamFromBackend(response.data.exam);
  },

  // Start exam session
  startSession: async (examId: string): Promise<ExamSession> => {
    const response = await api.post("/api/sessions/start", { examId });
    return response.data.session;
  },

  /**
   * Start the writing-duration clock when the student lands on question 1.
   * Safe to call repeatedly — only the first call moves the clock.
   */
  beginWritingClock: async (
    sessionId: string,
  ): Promise<{ startTime: string; writingStartTime?: string | null }> => {
    try {
      const response = await api.post(
        `/api/sessions/${encodeURIComponent(sessionId)}/begin-writing-clock`,
      );
      return response.data.session;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) throw error;
      const response = await axios.post(
        `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/begin-writing-clock-public`,
      );
      return response.data.session;
    }
  },

  // Get session by ID
  getSession: async (sessionId: string): Promise<ExamSession> => {
    const response = await api.get(`/api/sessions/${sessionId}`);
    return response.data.session;
  },

  getSessionPublic: async (sessionId: string): Promise<ExamSession> => {
    const response = await axios.get(
      `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/public`,
    );
    return response.data.session as ExamSession;
  },

  getSessionRegisteredPhoto: async (
    sessionId: string,
  ): Promise<SessionRegisteredPhotoResponse> => {
    const response = await axios.get(
      `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/registered-photo`,
    );
    return response.data as SessionRegisteredPhotoResponse;
  },

  // Submit exam (mock/IMMEDIATE exams return scored results in the same response)
  submitExam: async (
    sessionId: string,
    options?: { autoSubmitted?: boolean },
  ): Promise<ExamSubmitResponse> => {
    const response = await api.post("/api/exams/submit", {
      sessionId,
      ...(options?.autoSubmitted ? { autoSubmitted: true } : {}),
    });
    return response.data as ExamSubmitResponse;
  },

  /** Fetch individual results: GET /api/exams/exam-results/:examId/:studentId */
  getExamResults: async (examId: string, studentId: string): Promise<ExamResultsPayload> => {
    const response = await api.get(
      `/api/exams/exam-results/${encodeURIComponent(examId)}/${encodeURIComponent(studentId)}`
    );
    return (response.data?.data || response.data) as ExamResultsPayload;
  },

  storeImmediateResults: (examId: string, results: ExamResultsPayload): void => {
    const payload = { ...results, examId, _storedAt: new Date().toISOString() };
    try {
      sessionStorage.setItem(`examResult:${examId}`, JSON.stringify(payload));
      sessionStorage.setItem("lastExamResult", JSON.stringify(payload));
      return;
    } catch {
      /* quota: store compact summary without detailed question review */
    }
    try {
      const summary = { ...(payload as ExamResultsPayload & {
        detailedResults?: unknown;
        _storedAt?: string;
      }) };
      delete summary.detailedResults;
      sessionStorage.setItem(`examResult:${examId}`, JSON.stringify(summary));
      sessionStorage.setItem("lastExamResult", JSON.stringify(summary));
    } catch {
      /* ignore private mode */
    }
  },

  readStoredImmediateResults: (examId: string): ExamResultsPayload | null => {
    try {
      const raw = sessionStorage.getItem(`examResult:${examId}`);
      if (raw) return JSON.parse(raw) as ExamResultsPayload;
      const last = sessionStorage.getItem("lastExamResult");
      if (!last) return null;
      const parsed = JSON.parse(last) as ExamResultsPayload & { examId?: string };
      if (parsed?.examId && parsed.examId !== examId) return null;
      return parsed;
    } catch {
      return null;
    }
  },

  // Save single question response during exam (auto-save per answer)
  saveExamResponse: async (
    sessionId: string,
    questionId: string,
    response: string | string[] | Record<string, unknown>,
  ): Promise<void> => {
    await api.post("/api/exams/save-response", {
      sessionId,
      questionId,
      response,
    });
  },

  // Get all sessions for an exam (admin/proctor only)
  getExamSessions: async (
    examId: string,
    scope: "active" | "all" = "active",
    options?: { recentSeconds?: number }
  ): Promise<MonitorSessionPayload[]> => {
    const response = await api.get(`/api/sessions/exam/${examId}`, {
      timeout: 10000,
      params: {
        scope,
        ...(options?.recentSeconds ? { recentSeconds: options.recentSeconds } : {}),
      },
    });
    return (response.data.sessions || []) as MonitorSessionPayload[];
  },

  // Get active sessions count for an exam
  getActiveSessionsCount: async (examId: string): Promise<number> => {
    const response = await api.get(`/api/sessions/exam/${examId}/active-count`);
    return response.data.count || 0;
  },

  // Save photos to session
  savePhotos: async (
    sessionId: string,
    userPhoto?: string,
    idCardPhoto?: string
  ): Promise<void> => {
    try {
      await api.post(`/api/sessions/${sessionId}/photos`, {
        userPhoto,
        idCardPhoto,
      });
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) throw error;
      await axios.post(
        `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/photos-public`,
        { userPhoto, idCardPhoto },
      );
    }
  },

  // Save room scan video URL to session after S3 upload on backend
  saveRoomScanVideo: async (sessionId: string, videoDataUrl: string): Promise<void> => {
    try {
      await api.post(`/api/sessions/${sessionId}/room-scan-video`, {
        videoDataUrl,
      });
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) throw error;
      await axios.post(
        `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/room-scan-video-public`,
        { videoDataUrl },
      );
    }
  },

  /** Upload handwritten answer-sheet page to Examinations/Answer-Sheets. */
  saveHandwrittenAnswerPage: async (
    sessionId: string,
    payload: {
      questionId: string;
      pageNumber: number;
      imageDataUrl: string;
      candidateCode?: string;
    },
  ): Promise<{
    page: { pageNumber: number; url: string; questionId: string };
    answer: {
      mode: "handwritten";
      candidateCode: string;
      pages: Array<{ pageNumber: number; url: string }>;
    };
  }> => {
    try {
      const response = await api.post(
        `/api/sessions/${sessionId}/handwritten-answers`,
        payload,
      );
      return response.data;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) throw error;
      const response = await axios.post(
        `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/handwritten-answers-public`,
        payload,
      );
      return response.data;
    }
  },

  /** Delete one handwritten answer-sheet page from Redis + disk. */
  deleteHandwrittenAnswerPage: async (
    sessionId: string,
    payload: { questionId: string; pageNumber: number },
  ): Promise<{
    deletedPageNumber: number;
    answer: {
      mode: "handwritten";
      candidateCode: string;
      pages: Array<{ pageNumber: number; url: string }>;
    };
  }> => {
    try {
      const response = await api.delete(
        `/api/sessions/${sessionId}/handwritten-answers`,
        { data: payload },
      );
      return response.data;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) throw error;
      const response = await axios.delete(
        `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/handwritten-answers-public`,
        { data: payload },
      );
      return response.data;
    }
  },

  /** Reorder handwritten pages. orderedPageNumbers = previous page numbers in new order. */
  reorderHandwrittenAnswerPages: async (
    sessionId: string,
    payload: {
      questionId: string;
      orderedPageNumbers: number[];
      candidateCode?: string;
    },
  ): Promise<{
    answer: {
      mode: "handwritten";
      candidateCode: string;
      pages: Array<{ pageNumber: number; url: string }>;
    };
  }> => {
    try {
      const response = await api.put(
        `/api/sessions/${sessionId}/handwritten-answers/reorder`,
        payload,
      );
      return response.data;
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) throw error;
      const response = await axios.put(
        `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/handwritten-answers-public/reorder`,
        payload,
      );
      return response.data;
    }
  },

  /** Upload proctored live exam video+audio chunk to Examinations/Recordings. */
  saveLiveRecording: async (sessionId: string, videoDataUrl: string): Promise<void> => {
    try {
      await api.post(`/api/sessions/${sessionId}/live-recording`, {
        videoDataUrl,
      });
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status !== 401 && status !== 403) throw error;
      await axios.post(
        `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/live-recording-public`,
        { videoDataUrl },
      );
    }
  },

  // Get LiveKit access token for CCTV-style live monitoring
  getLivekitToken: async (
    sessionId: string,
    mode: "publisher" | "subscriber" = "publisher",
  ): Promise<LivekitTokenResponse> => {
    try {
      let response;
      try {
        response = await api.get(`/api/sessions/${sessionId}/livekit-token`);
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status !== 401 && status !== 403) throw error;
        const publicPath =
          mode === "subscriber"
            ? `/api/sessions/${encodeURIComponent(sessionId)}/livekit-token-public?mode=subscriber`
            : `/api/sessions/${encodeURIComponent(sessionId)}/livekit-token-public`;
        response = await axios.get(`${API_BASE_URL}${publicPath}`);
      }
      return response.data as LivekitTokenResponse;
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { error?: string; message?: string } };
        message?: string;
      };
      const detail =
        axiosError.response?.data?.error ||
        axiosError.response?.data?.message ||
        axiosError.message ||
        "Failed to fetch LiveKit token";
      throw new Error(detail);
    }
  },

  // Start exam with passkey (URL-based student authentication)
  startExamWithPasskey: async (
    applicantId: string,
    applicationNo: string,
    passkey: string
  ): Promise<{
    sessionId: string;
    examId: string;
    setCode: string;
    duration: string | number;
    token: string;
    user: {
      id: string;
      name: string;
      email: string;
      role: string;
      studentId: string;
    };
    resumed: boolean;
    message?: string;
    isMock?: boolean;
  }> => {
    const response = await api.post("/api/exams/start", {
      applicantId,
      applicationNo,
      passkey,
    });
    return response.data;
  },
};

